import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert, InputGroup } from "react-bootstrap";
import moment from "moment";

import { getClientes } from "../helpers/rutaClientes";
import { getProveedores } from "../helpers/rutaProveedores";
import { getTransportes } from "../helpers/rutaTransportes";
import { getChoferes } from "../helpers/rutaChoferes";
import { getLotes } from "../helpers/rutaLotes";

import { postRemitoSaldoInicial } from "../helpers/rutaRemitos";

const IVA_105 = 1.105;

const sortBy = (arr, key) =>
  [...(arr || [])].sort((a, b) =>
    (a?.[key] || "").localeCompare(b?.[key] || "", "es", { sensitivity: "base" })
  );

const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const parseARS = (s = "") => {
  const clean = String(s).replace(/[^\d,.-]/g, "");
  const normalized = clean.replace(/\./g, "").replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const findSaldoInicial = (list = [], getName) =>
  list.find((x) => String(getName(x) || "").toUpperCase().includes("SALDO INICIAL"));

const loteLabel = (l) =>
  l?.codlote != null ? `#${l.codlote} - ${l.lote || ""}` : (l?.lote || "(sin lote)");

export default function ModalSaldoInicialRemito({ show, onClose, onSaved }) {
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [transportes, setTransportes] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [lotes, setLotes] = useState([]);

  const [saldoClienteId, setSaldoClienteId] = useState("");
  const [saldoProveedorId, setSaldoProveedorId] = useState("");

  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    codrem: "",
    fecha: moment().format("YYYY-MM-DD"),
    tipo: "PROVEEDOR", // PROVEEDOR | CLIENTE
    cliente: "",
    proveedor: "",
    transporte: "",
    chofer: "",
    lote: "",
    saldoA: "", // blanco (con IVA incluido en lo que carga el usuario)
    saldoB: "", // negro
    observaciones: "SALDO INICIAL",
  });

  useEffect(() => {
    if (!show) return;

    setErr("");
    setSaving(false);

    (async () => {
      try {
        const [cRaw, pRaw, tRaw, chRaw, lRaw] = await Promise.all([
          getClientes(),
          getProveedores(),
          getTransportes(),
          getChoferes(),
          getLotes(),
        ]);

        const cList = Array.isArray(cRaw) ? cRaw : cRaw?.clientes || cRaw?.data || [];
        const pList = Array.isArray(pRaw) ? pRaw : pRaw?.proveedores || pRaw?.data || [];
        const tList = Array.isArray(tRaw) ? tRaw : tRaw?.transportes || tRaw?.data || [];
        const chList = Array.isArray(chRaw) ? chRaw : chRaw?.choferes || chRaw?.data || [];
        const lList = Array.isArray(lRaw) ? lRaw : lRaw?.lotes || lRaw?.data || [];

        const cSorted = sortBy(cList, "razonsocial");
        const pSorted = sortBy(pList, "razonsocial");
        const tSorted = sortBy(tList, "transporte");
        const chSorted = sortBy(chList, "nombre");
        const lSorted = sortBy(lList, "lote");

        setClientes(cSorted);
        setProveedores(pSorted);
        setTransportes(tSorted);
        setChoferes(chSorted);
        setLotes(lSorted);

        const sc = findSaldoInicial(cSorted, (x) => x?.razonsocial || x?.nombre || x?.razonSocial);
        const sp = findSaldoInicial(pSorted, (x) => x?.razonsocial || x?.nombre || x?.razonSocial);

        setSaldoClienteId(sc?._id || "");
        setSaldoProveedorId(sp?._id || "");

        // Buscar "SALDO INICIAL" también en Transporte / Chofer / Lote
        const st = findSaldoInicial(tSorted, (x) => x?.transporte || x?.nombre);
        const sch = findSaldoInicial(chSorted, (x) => x?.nombre);
        const sl = findSaldoInicial(lSorted, (x) => x?.lote);

        // ✅ Preseleccionar SOLO si está vacío (no pisa lo que el usuario ya tocó)
        setForm((f) => ({
        ...f,
        transporte: f.transporte || (st?._id || ""),
        chofer: f.chofer || (sch?._id || ""),
        lote: f.lote || (sl?._id || ""),
        }));


        // Prefill opcional: si tenés “SALDO INICIAL” en transporte/chofer/lote, dejalos seleccionados manualmente.
        // (No los auto-seteo acá porque depende de tu data real y no quiero "pisarte" selecciones.)
      } catch (e) {
        console.error("❌ Error cargando combos:", e);
        setErr("No se pudieron cargar listas (clientes/proveedores/transportes/choferes/lotes).");
      }
    })();
  }, [show]);



  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const onChangeTipo = (e) => {
    const tipo = e.target.value;
    setForm((s) => ({
      ...s,
      tipo,
      cliente: "",
      proveedor: "",
    }));
  };

  const onMoneyBlur = (name) => {
    setForm((s) => {
      const n = parseARS(s[name]);
      return { ...s, [name]: n ? fmtARS(n) : "" };
    });
  };

  const canSave = useMemo(() => {
    const nro = Number(form.codrem);
    if (!Number.isFinite(nro) || nro < 1 || nro > 100) return false;

    if (form.tipo === "PROVEEDOR" && !form.proveedor) return false;
    if (form.tipo === "CLIENTE" && !form.cliente) return false;

    if (!form.transporte) return false;
    if (!form.chofer) return false;
    if (!form.lote) return false;

    if (!saldoClienteId || !saldoProveedorId) return false;

    return true;
  }, [form, saldoClienteId, saldoProveedorId]);

    const guardar = async () => {
    if (saving) return;

    try {
        setErr("");
        setSaving(true);

        const esProv = form.tipo === "PROVEEDOR";
        const esCli  = form.tipo === "CLIENTE";

        // 👇 En saldo inicial, A YA VIENE con IVA incluido (como vos querés)
        const a = parseARS(form.saldoA); // blanco + IVA incluido
        const b = parseARS(form.saldoB); // negro
        const total = a + b;

        const payload = {
        codrem: Number(form.codrem),
        fecha: moment(form.fecha, ["YYYY-MM-DD", "DD/MM/YYYY"]).format("YYYY-MM-DD"),
        observaciones: form.observaciones,
        saldoInicial: true,

        // Siempre ambos: uno real y la contraparte SALDO INICIAL
        proveedor: esProv ? form.proveedor : saldoProveedorId,
        cliente:   esCli  ? form.cliente   : saldoClienteId,

        transporte: form.transporte,
        chofer: form.chofer,
        lote: form.lote,

        // 👇 COMPRAS (solo si es PROVEEDOR)
        totalCompraA: esProv ? Number(a.toFixed(2)) : 0,   // A con IVA
        totalCompraB: esProv ? Number(b.toFixed(2)) : 0,
        totalCompra:  esProv ? Number(total.toFixed(2)) : 0,

        // 👇 VENTAS (solo si es CLIENTE)
        totalVentaA:  esCli ? Number(a.toFixed(2)) : 0,    // A con IVA
        totalVentaB:  esCli ? Number(b.toFixed(2)) : 0,
        totalVenta:   esCli ? Number(total.toFixed(2)) : 0,

        factura: false,
        flete: 0,

        // relleno defensivo (tu schema los pide)
        kgCampo: 0,
        kgRecibido: 0,
        precioCompra: 0,
        precioVenta: 0,
        diferencia: 0,
        comision: 0,
        };

        const r = await postRemitoSaldoInicial(payload);

        if (r?.ok === false) {
        const msg = r?.err?.message || r?.msg || "El back devolvió ok:false.";
        throw new Error(msg);
        }

        onSaved?.();
        onClose?.();
    } catch (e) {
        const data = e?.response?.data;
        const msg =
        data?.err?.message ??
        data?.msg ??
        data?.error ??
        data?.message ??
        (typeof data === "string" ? data : data ? JSON.stringify(data) : "") ??
        e?.message ??
        "Error guardando saldo inicial.";
        setErr(msg);
    } finally {
        setSaving(false);
    }
    };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Saldo Inicial (Remitos 1..100)</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {err ? <Alert variant="danger">{err}</Alert> : null}

        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Nro Remito (manual 1..100)</Form.Label>
              <Form.Control
                name="codrem"
                value={form.codrem}
                onChange={onChange}
                type="number"
                min={1}
                max={100}
                placeholder="Ej: 1"
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Fecha</Form.Label>
              <Form.Control name="fecha" value={form.fecha} onChange={onChange} type="date" />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Tipo</Form.Label>
              <Form.Select name="tipo" value={form.tipo} onChange={onChangeTipo}>
                <option value="PROVEEDOR">Proveedor</option>
                <option value="CLIENTE">Cliente</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {form.tipo === "PROVEEDOR" ? (
            <>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Proveedor</Form.Label>
                  <Form.Select name="proveedor" value={form.proveedor} onChange={onChange}>
                    <option value="">Seleccionar…</option>
                    {proveedores.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.razonsocial || p.nombre || p.razonSocial || "(sin nombre)"}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Cliente (contraparte automática)</Form.Label>
                  <Form.Control value="SALDO INICIAL" disabled />
                </Form.Group>
              </Col>
            </>
          ) : (
            <>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Cliente</Form.Label>
                  <Form.Select name="cliente" value={form.cliente} onChange={onChange}>
                    <option value="">Seleccionar…</option>
                    {clientes.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.razonsocial || c.nombre || c.razonSocial || "(sin nombre)"}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Proveedor (contraparte automática)</Form.Label>
                  <Form.Control value="SALDO INICIAL" disabled />
                </Form.Group>
              </Col>
            </>
          )}

          <Col md={4}>
            <Form.Group>
              <Form.Label>Transporte (requerido)</Form.Label>
              <Form.Select name="transporte" value={form.transporte} onChange={onChange}>
                <option value="">Seleccionar…</option>
                {transportes.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.transporte || t.nombre || "(sin transporte)"}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Chofer (requerido)</Form.Label>
              <Form.Select name="chofer" value={form.chofer} onChange={onChange}>
                <option value="">Seleccionar…</option>
                {choferes.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.nombre || "(sin nombre)"}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Lote (requerido)</Form.Label>
              <Form.Select name="lote" value={form.lote} onChange={onChange}>
                <option value="">Seleccionar…</option>
                {lotes.map((l) => (
                  <option key={l._id} value={l._id}>
                    {loteLabel(l)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>Saldo A (blanco, con IVA incluido)</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  name="saldoA"
                  value={form.saldoA}
                  onChange={onChange}
                  onBlur={() => onMoneyBlur("saldoA")}
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  style={{ color: "#b00020", fontWeight: 700, textAlign: "right" }}
                />
              </InputGroup>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>Saldo B (negro)</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  name="saldoB"
                  value={form.saldoB}
                  onChange={onChange}
                  onBlur={() => onMoneyBlur("saldoB")}
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  style={{ color: "#b00020", fontWeight: 700, textAlign: "right" }}
                />
              </InputGroup>
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group>
              <Form.Label>Observaciones</Form.Label>
              <Form.Control
                name="observaciones"
                value={form.observaciones}
                onChange={onChange}
                type="text"
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={guardar} disabled={!canSave || saving}>
          {saving ? "Guardando…" : "Guardar saldo inicial"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
